import { h, Fragment } from 'preact';
import { useState, useEffect, useMemo } from 'preact/hooks';
import TrapFocus from 'utils/trap-focus';
import Close from 'utils/icon/icon-close.svg';
import clsx from 'clsx';
import { Transition } from '@headlessui/react';
import SizeGuideTab from './size-guide-tab';

const SizeGuide = ({ data }) => {
  const { sizeGuideData } = data;
  const [isOpened, setIsOpened] = useState(false);
  const [sizeGuides, setSizeGuides] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [isEmpty, setIsEmpty] = useState(false);

  const handleExternalSizeGuide = (e) => {
    const { handles } = e.detail;
    if (!handles.length) {
      setIsEmpty(true);
    } else {
      setSizeGuides(handles);
      setActiveTab(handles[0]);
    }
    setIsOpened(true);
  };

  const sizeGuideTabs = useMemo(() => {
    if (sizeGuides.length === 0) return [];

    const tabs = sizeGuides.map((handle) => {
      const tabData = sizeGuideData[handle];

      return {
        handle,
        category: tabData?.category ?? handle,
        data: tabData,
      };
    });

    return tabs;
  }, [sizeGuides]);

  const handleClose = () => {
    setIsOpened(false);
  };

  let trapFocus = null;

  useEffect(() => {
    if (isOpened) {
      if (!trapFocus)
        trapFocus = new TrapFocus(
          document.querySelector('.size-guide__header'),
        );
      document.body.classList.add('overflow-hidden');
      trapFocus.init(true);
    }

    return () => {
      // Checking for the presence of the class '.look-overlay__content' or '.quick-shop form'
      // to prevent removing 'overflow: hidden' from the body if "shop the look" is still open.
      if (!document.querySelector('.look-overlay__content') && !document.querySelector('.quick-shop form')) {
        document.body.classList.remove('overflow-hidden');
      }
      if (trapFocus)
        trapFocus.destroy(document.querySelector(`.section--size`));
    };
  }, [isOpened, trapFocus]);

  useEffect(() => {
    document.addEventListener('size-guide:open', handleExternalSizeGuide);

    return () => {
      document.removeEventListener('size-guide:open', handleExternalSizeGuide);
    };
  }, []);

  const currentSizeGuide = useMemo(() => {
    if (!activeTab) return null;

    const sizeGuide = sizeGuideTabs.find((tab) => tab.handle === activeTab);
    return sizeGuide?.data ?? null;
  }, [activeTab, sizeGuides]);

  return (
    <>
      <div
        className={clsx(
          'size-guide fixed flex flex-col w-full top-0 bottom-0 right-0  z-[1003] bg-white md:w-[507px] transition-transform duration-300',
          {
            'translate-x-0': isOpened,
            'translate-x-full transition-all': !isOpened,
          },
        )}
      >
        <div className="size-guide__header flex justify-between px-24 py-28 border-b border-b-grey-400">
          <span className="heading-3 leading-100" data-size-guide-title>
            {currentSizeGuide?.title || ''}
          </span>
          <button
            className="size-guide-flyout__button"
            aria-label={window.TMW.Strings.icons.close}
            title={window.TMW.Strings.icons.close}
            tabIndex="0"
            onClick={handleClose}
          >
            <Close className="w-24" />
          </button>
        </div>
        <div className='className="h-[calc(100%-81px)] overflow-scroll hide-scrollbar'>
          {isEmpty ? (
            <div className="p-24 text-center body-2">
              {window.TMW.Strings.product.noSizeGuide}
            </div>
          ) : (
            <div className="size-guide__details p-24">
              {sizeGuideTabs.length > 1 && currentSizeGuide && (
                <div
                  className="flex justify-start gap-40 ml:justify-center mb-24"
                  role="tablist"
                  aria-orientation="horizontal"
                >
                  {sizeGuideTabs.map(({ handle, category }, index) => (
                    <button
                      onClick={() => setActiveTab(handle)}
                      role="tab"
                      aria-selected={activeTab === handle}
                      id={`${handle}-tab`}
                      aria-controls={`${handle}-panel`}
                      className={clsx(
                        'pb-8 body-2-medium border-b hover:text-black hover:border-black',
                        {
                          'border-black': activeTab === handle,
                          'text-subdued border-transparent':
                            activeTab !== handle,
                        },
                      )}
                      key={index}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              )}
              {currentSizeGuide ? (
                <SizeGuideTab sizeGuide={currentSizeGuide} />
              ) : (
                <div className="text-center body-2">
                  {window.TMW.Strings.product.noSizeGuide}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Transition
        show={isOpened}
        as={Fragment}
        enter="transition-opacity ease-linear duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity ease-linear duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div
          className="fixed inset-0 bg-black/20 z-[1002]"
          aria-hidden="true"
          onClick={() => handleClose()}
        />
      </Transition>
    </>
  );
};

export default SizeGuide;
